import PublicLayout from "@/components/PublicLayout";

export default function PrivacyPolicy() {
  return (
    <PublicLayout title="Privacy Policy" lastUpdated="Last Updated: June 2026"
      intro="NextRota respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, store, and protect information when you visit our website, create an account, use our workforce scheduling and employee management platform, or otherwise interact with our services.">

      <p>By accessing or using NextRota, you acknowledge that you have read and understood this Privacy Policy.</p>

      <h2>Information We Collect</h2>

      <h3>Information You Provide</h3>
      <p>We may collect information that you voluntarily provide, including:</p>
      <ul>
        <li>Name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Username</li>
        <li>Password</li>
        <li>Job title</li>
        <li>Company name</li>
        <li>Billing information</li>
        <li>Contact preferences</li>
        <li>Availability information</li>
        <li>Shift preferences</li>
        <li>Employee scheduling information</li>
        <li>Leave and absence information</li>
        <li>Messages and communications sent through our platform</li>
        <li>Information submitted through contact forms or support requests</li>
      </ul>

      <h3>Account Information</h3>
      <p>When you register for an account, we may collect information necessary to create and maintain your account and provide our services.</p>

      <h3>Payment Information</h3>
      <p>If you purchase a subscription or make a payment through NextRota, payment processing is handled by third-party payment processors such as Stripe. We do not store full payment card numbers on our systems.</p>

      <h3>Information Collected Automatically</h3>
      <p>When you use our website or services, we may automatically collect:</p>
      <ul>
        <li>IP address</li>
        <li>Browser type and version</li>
        <li>Device information</li>
        <li>Operating system</li>
        <li>Login activity</li>
        <li>Usage information</li>
        <li>Error logs</li>
        <li>Diagnostic information</li>
        <li>Session information</li>
        <li>Website interaction data</li>
      </ul>

      <h3>Cookies and Similar Technologies</h3>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Keep users logged in</li>
        <li>Maintain security</li>
        <li>Remember preferences</li>
        <li>Improve functionality</li>
        <li>Analyse service performance</li>
      </ul>
      <p>Further details are available in our <a href="/cookie-policy">Cookie Policy</a>.</p>

      <h2>How We Use Information</h2>
      <p>We may use personal information to:</p>
      <ul>
        <li>Provide and operate our services</li>
        <li>Create and manage user accounts</li>
        <li>Deliver workforce scheduling functionality</li>
        <li>Facilitate employee and manager communications</li>
        <li>Process subscriptions and payments</li>
        <li>Respond to enquiries and support requests</li>
        <li>Send administrative communications</li>
        <li>Improve our services</li>
        <li>Monitor platform performance</li>
        <li>Detect, prevent, and investigate fraud or security incidents</li>
        <li>Comply with legal obligations</li>
        <li>Enforce our terms and policies</li>
      </ul>

      <h2>Legal Bases for Processing (EEA and UK Users)</h2>
      <p>Where applicable, we rely on the following legal bases:</p>

      <h3>Performance of a Contract</h3>
      <p>We process information where necessary to provide our services and fulfil our contractual obligations.</p>

      <h3>Legitimate Interests</h3>
      <p>We process information where necessary for our legitimate business interests, including:</p>
      <ul>
        <li>Improving our services</li>
        <li>Analysing usage trends</li>
        <li>Protecting our platform</li>
        <li>Preventing fraud</li>
        <li>Responding to user feedback</li>
      </ul>

      <h3>Consent</h3>
      <p>Where required by law, we rely on consent for specific processing activities.</p>

      <h3>Legal Obligation</h3>
      <p>We may process information to comply with applicable laws and regulatory requirements.</p>

      <h3>Vital Interests</h3>
      <p>In limited circumstances, we may process information to protect the safety or vital interests of an individual.</p>

      <h2>Sharing of Information</h2>
      <p>We may disclose personal information to:</p>
      <ul>
        <li>Cloud hosting providers</li>
        <li>Data storage providers</li>
        <li>Authentication providers</li>
        <li>Payment processors</li>
        <li>AI service providers</li>
        <li>Communication and collaboration service providers</li>
        <li>Customer support providers</li>
        <li>Professional advisers</li>
        <li>Regulatory authorities when required by law</li>
      </ul>
      <p>We do not sell personal information to data brokers.</p>

      <h2>International Transfers</h2>
      <p>Personal information may be transferred to and processed in countries outside the United Kingdom or European Economic Area. Where required, we implement appropriate safeguards, including Standard Contractual Clauses approved by the European Commission or other lawful transfer mechanisms.</p>

      <h2>Data Retention</h2>
      <p>We retain personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, enforce agreements, and maintain business records. In most cases, personal information will be retained while a user maintains an active account with NextRota. Following account closure, certain information may be retained where required by law, regulatory obligations, fraud prevention, accounting requirements, or legitimate business interests.</p>

      <h2>Data Security</h2>
      <p>We implement reasonable technical and organisational measures designed to protect personal information from unauthorised access, loss, misuse, disclosure, alteration, or destruction. However, no method of electronic transmission or storage is completely secure. While we strive to protect personal information, we cannot guarantee absolute security.</p>

      <h2>Your Privacy Rights</h2>
      <p>Depending on your location, you may have certain rights regarding your personal information.</p>

      <h3>Rights for UK and EEA Users</h3>
      <p>Subject to applicable law, you may have the right to:</p>
      <ul>
        <li>Access your personal information</li>
        <li>Correct inaccurate information</li>
        <li>Request deletion of personal information</li>
        <li>Restrict processing</li>
        <li>Object to processing</li>
        <li>Withdraw consent where consent is the legal basis</li>
        <li>Request portability of your information</li>
        <li>Lodge a complaint with a supervisory authority</li>
      </ul>

      <h3>Rights for US Residents</h3>
      <p>Residents of certain US states may have rights including:</p>
      <ul>
        <li>Accessing personal information</li>
        <li>Correcting personal information</li>
        <li>Deleting personal information</li>
        <li>Obtaining a copy of personal information</li>
        <li>Opting out of certain forms of processing where applicable</li>
        <li>Appealing decisions relating to privacy requests</li>
      </ul>
      <p>To exercise any privacy rights, please contact us using the contact information provided below.</p>

      <h3>California Privacy Rights</h3>
      <p>California residents may have additional rights under the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA). We may collect categories of personal information including:</p>
      <ul>
        <li>Identifiers</li>
        <li>Customer records information</li>
        <li>Internet or network activity information</li>
        <li>Professional or employment-related information</li>
        <li>Commercial information relating to subscriptions and purchases</li>
      </ul>
      <p>We do not knowingly sell personal information. California residents may request information regarding the collection, use, disclosure, and deletion of their personal information, subject to applicable legal limitations.</p>

      <h2>Marketing Communications</h2>
      <p>Where permitted by law, we may send marketing communications regarding our services, features, updates, and promotions. Users may opt out of marketing communications at any time by:</p>
      <ul>
        <li>Using the unsubscribe link included in marketing emails</li>
        <li>Contacting us directly</li>
      </ul>
      <p>Administrative and service-related communications may still be sent where necessary.</p>

      <h2>Artificial Intelligence Features</h2>
      <p>NextRota may use artificial intelligence and automated technologies to support workforce scheduling, resource planning, reporting, forecasting, recommendations, and related operational functions. Where AI features are provided, we will take reasonable steps to ensure their responsible use and compliance with applicable privacy laws.</p>

      <h2>Third-Party Websites</h2>
      <p>Our website and services may contain links to third-party websites or services. We are not responsible for the privacy practices, content, or policies of third-party websites. Users should review the privacy policies of any external sites they visit.</p>

      <h2>Children’s Privacy</h2>
      <p>NextRota is not directed to children under the age of 18. We do not knowingly collect personal information from children. If we become aware that personal information has been collected from a child in violation of applicable laws, we will take reasonable steps to delete such information.</p>

      <h2>Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy from time to time. When material changes are made, we may update the “Last Updated” date and, where required by law, provide additional notice to users. Continued use of the services following publication of an updated Privacy Policy constitutes acceptance of the revised policy.</p>

      <h2>Contact Us</h2>
      <p>If you have questions regarding this Privacy Policy, your personal information, or your privacy rights, please contact:</p>
      <p>
        <strong>NextRota</strong><br/>
        Email: <a href="mailto:privacy@nextrota.com">privacy@nextrota.com</a><br/>
        Website: <a href="https://nextrota.com">https://nextrota.com</a>
      </p>
      <p>For privacy requests, including access, correction, deletion, or appeals relating to privacy requests, please contact us using the email address above.</p>

      <h2>Complaints</h2>
      <p>If you are located in the United Kingdom, European Union, or another jurisdiction that provides regulatory complaint rights, you may have the right to lodge a complaint with the relevant supervisory authority if you believe your privacy rights have been violated. We encourage users to contact us first so we can attempt to resolve any concerns directly.</p>
    </PublicLayout>
  );
}
